import { useEffect, useMemo, useReducer, useRef } from "react";
import { AccountTypeToggle } from "./account-type-toggle";
import { BasePlanCards } from "./base-plan-cards";
import { RoomInputs } from "./room-inputs";
import { ModuleToggles } from "./module-toggles";
import { BillPanel } from "./bill-panel";
import {
  defaultSelection,
  planSelectionReducer,
  quoteForSelection,
  serializeSelection,
  type BillBreakdown,
  type PlanSelection,
} from "./plan-selection";

export type PlanCalculatorMode = "prospect" | "account";

/**
 * The modular pricing calculator — account type → base plan → two room
 * inputs → modules → live itemized bill. One component, three hosts: the
 * public /pricing page (prospect mode, URL-synced state), onboarding
 * payment, and the in-app plan editor (account mode, hydrated from
 * GET /api/app/account/plan with REAL per-owner waiver data).
 *
 * All live math is the shared computeBill — the same function the server
 * quotes and bills with, so the display can never drift from the charge.
 */
export function PlanCalculator({
  mode,
  initialSelection,
  realOwnerData,
  ctaLabel,
  ctaPending,
  onSubmit,
  onSelectionChange,
  footnote,
}: {
  mode: PlanCalculatorMode;
  initialSelection?: Partial<PlanSelection>;
  /** Account mode only: authoritative per-owner portal/waiver counts. */
  realOwnerData?: { portalOwners: number; waivedOwners: number };
  ctaLabel: string;
  ctaPending?: boolean;
  onSubmit: (selection: PlanSelection, quote: BillBreakdown) => void;
  /** Prospect mode: fires (debounced by the caller if needed) for URL sync. */
  onSelectionChange?: (selection: PlanSelection, serialized: string) => void;
  footnote?: React.ReactNode;
}) {
  const [selection, dispatch] = useReducer(planSelectionReducer, undefined, () =>
    planSelectionReducer(defaultSelection(), { type: "hydrate", selection: initialSelection ?? {} }),
  );

  const quote = useMemo(
    () => quoteForSelection(selection, mode === "account" ? realOwnerData : undefined),
    [selection, mode, realOwnerData],
  );

  // Prospect URL sync — debounced so slider drags don't spam replaceState.
  const changeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!onSelectionChange) return;
    if (changeTimer.current) clearTimeout(changeTimer.current);
    changeTimer.current = setTimeout(() => {
      onSelectionChange(selection, serializeSelection(selection));
    }, 300);
    return () => {
      if (changeTimer.current) clearTimeout(changeTimer.current);
    };
  }, [selection, onSelectionChange]);

  return (
    <div className="grid lg:grid-cols-[1fr_380px] gap-8 items-start" data-testid="plan-calculator">
      <div className="space-y-8">
        <section>
          <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-3">1 · Account type</h3>
          <AccountTypeToggle
            value={selection.accountKind}
            onChange={(accountKind) => dispatch({ type: "setAccountKind", accountKind })}
          />
        </section>

        <section>
          <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-3">2 · Base plan</h3>
          <BasePlanCards
            accountKind={selection.accountKind}
            value={selection.planTier}
            onChange={(planTier) => dispatch({ type: "setPlanTier", planTier })}
          />
        </section>

        <section>
          <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-3">3 · Your portfolio</h3>
          <RoomInputs
            roomsOnPadsplit={selection.roomsOnPadsplit}
            roomsOffPadsplit={selection.roomsOffPadsplit}
            onChangeOn={(value) => dispatch({ type: "setRoomsOn", value })}
            onChangeOff={(value) => dispatch({ type: "setRoomsOff", value })}
          />
        </section>

        <section>
          <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-3">4 · Modules & add-ons</h3>
          <ModuleToggles
            selection={selection}
            dispatch={dispatch}
            realOwnerData={mode === "account" ? realOwnerData : undefined}
          />
        </section>
      </div>

      <div className="lg:sticky lg:top-24">
        <BillPanel
          selection={selection}
          quote={quote}
          ctaLabel={ctaLabel}
          ctaPending={ctaPending}
          onSubmit={() => onSubmit(selection, quote)}
          onSwitchToPro={() => dispatch({ type: "setPlanTier", planTier: "pro" })}
          footnote={footnote}
        />
      </div>
    </div>
  );
}
